import React from 'react';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { Badge } from '../ui/badge';
import { MODULE_LABELS } from '../iad/constants';

export const Insufficient = () => <Badge variant="outline" className="text-slate-500">Muestra insuficiente</Badge>;
const pct = (v) => (v == null ? '—' : `${v}%`);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString('es', { maximumFractionDigits: 2, notation: Math.abs(v) >= 1e6 ? 'compact' : 'standard' }));
const tone = (r) => (r == null ? '' : r >= 70 ? 'text-emerald-600 dark:text-emerald-400' : r >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400');

export const Section = ({ title, subtitle, children, testid }) => (
  <section className="iad-reveal space-y-3 rounded-2xl border border-slate-200 p-5 dark:border-slate-700" data-testid={testid}>
    <div><h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>{subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}</div>
    {children}
  </section>
);

export const Table = ({ head, rows, empty, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="text-left text-xs uppercase text-slate-500"><tr>{head.map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
      <tbody>{rows}</tbody>
    </table>
    {!rows.length && <p className="p-4 text-center text-sm text-slate-500">{empty}</p>}
  </div>
);

const Rate = ({ r, reliable }) => (reliable ? <span className={`text-lg font-bold ${tone(r)}`}>{pct(r)}</span> : <Insufficient />);

export const OutcomeRow = ({ name, x, extra, testid }) => (
  <tr className="border-t border-slate-200 dark:border-slate-700" data-testid={testid}>
    <td className="p-2 font-medium">{name}</td>
    <td className="p-2"><Rate r={x.hit_rate} reliable={x.reliable} /></td>
    <td className="p-2 text-xs text-slate-500">{x.hits} cumplidas · {x.partial} parciales · {x.misses} no cumplidas · {x.expired} vencidas</td>
    <td className="p-2">{pct(x.avg_achievement)}</td><td className="p-2">{pct(x.avg_abs_deviation)}</td>
    {extra}
  </tr>
);

export const ModuleRow = ({ m }) => (
  <tr className="border-t border-slate-200 dark:border-slate-700" data-testid={`hist-module-${m.module_type}`}>
    <td className="p-2 font-medium">{MODULE_LABELS[m.module_type] || m.module_type}</td><td className="p-2">{m.projects}</td>
    <td className="p-2">{m.reliable ? <b>{m.avg_spi ?? '—'}</b> : <Insufficient />}</td><td className="p-2">{pct(m.late_rate)}</td><td className="p-2">{m.completed}</td>
  </tr>
);

export const AssigneeRow = ({ a }) => (
  <tr className="border-t border-slate-200 dark:border-slate-700" data-testid={`hist-assignee-${a.user_id}`}>
    <td className="p-2 font-medium">{a.name}</td><td className="p-2">{a.activities}</td><td className="p-2">{a.completed}</td>
    <td className="p-2"><Rate r={a.on_time_rate} reliable={a.reliable} /></td>
    <td className={`p-2 ${a.overdue_open ? 'font-semibold text-red-600 dark:text-red-400' : ''}`}>{a.overdue_open}</td>
  </tr>
);

export const KpiRow = ({ k }) => (
  <tr className="border-t border-slate-200 dark:border-slate-700" data-testid={`hist-kpi-${k.kpi_name}`}>
    <td className="p-2"><p className="font-medium">{k.kpi_name}</p><p className="text-[11px] text-slate-500">{MODULE_LABELS[k.module_type] || k.module_type || '—'} · {k.points} mediciones</p></td>
    <td className="w-32 p-2"><div className="h-8"><ResponsiveContainer width="100%" height="100%"><LineChart data={k.series}>
      <Line dataKey="value" stroke="#38bdf8" strokeWidth={1.5} dot={false} isAnimationActive={false} /></LineChart></ResponsiveContainer></div></td>
    <td className={`p-2 font-semibold ${k.change_pct > 0 ? 'text-emerald-600 dark:text-emerald-400' : k.change_pct < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{k.change_pct == null ? '—' : `${k.change_pct > 0 ? '+' : ''}${k.change_pct}%`}</td>
    <td className="p-2">{pct(k.volatility_pct)}</td>
    <td className="p-2 text-xs">{num(k.best.value)} <span className="text-slate-500">({k.best.date})</span></td>
    <td className="p-2 text-xs">{num(k.worst.value)} <span className="text-slate-500">({k.worst.date})</span></td>
    <td className="p-2">{k.reliable ? num(k.slope_per_month) : <Insufficient />}</td>
  </tr>
);
